import { Link } from '@tanstack/react-router';
import { FileSignature } from 'lucide-react';
import type { ReactNode } from 'react';
import { OFFER_STATUS_LABELS, type JobDetail } from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { SkeletonLines } from '../../../components/Skeleton';
import { OFFER_STATUS_TONES } from '../../../components/status-tones';
import { ForbiddenState, ServerErrorState, SystemState } from '../../../components/system-states/SystemState';
import { ToneChip } from '../../../components/Tone';
import { formatDate, formatIsoDate } from '../../../lib/format';
import { useJobOffers } from '../api/offers-api';
import { money } from '../offer-format';
import styles from './Offers.module.css';

/**
 * JOB-10's Offers tab (OFR-03's list): every offer on the job — drafts to finish, offers with
 * the approvers or the candidate, and how each ended. One click opens the offer. The gross
 * figure reads `—` for the hiring manager (§L23).
 */
export function OffersTab({ detail }: { readonly detail: JobDetail }): ReactNode {
  const offers = useJobOffers(detail.id);
  if (offers.error) {
    if (offers.error.status === 403) return <ForbiddenState resource="this job's offers" contact="Your admin" />;
    return <ServerErrorState onRetry={() => void offers.refetch()} />;
  }
  if (offers.isPending) return <SkeletonLines count={4} />;
  const rows = offers.data;
  if (rows.length === 0) {
    return (
      <SystemState
        icon={FileSignature}
        isInset
        title="No offers yet"
        body="When the hiring manager or HR selects someone on the Compare tab, an offer draft appears here to fill in."
      />
    );
  }
  return (
    <Card title="Offers" meta={`${rows.length} on this job · newest first`} isFlush>
      <ul className={styles.list}>
        {rows.map((row) => (
          <li key={row.id}>
            <Link to="/offers/$offerId" params={{ offerId: row.id }} className={styles.row}>
              <span className={styles.rowName}>
                {row.candidateName}
                <span className={styles.meta}>{row.reference}</span>
              </span>
              <span>
                <ToneChip tone={OFFER_STATUS_TONES[row.status]}>{OFFER_STATUS_LABELS[row.status]}</ToneChip>
              </span>
              <span className={`${styles.figure} ${styles.rowHideMobile}`}>{money(row.offer.grossMonthlyMinor, row.offer.currency)}</span>
              <span className={`${styles.meta} ${styles.rowHideMobile}`}>
                {row.joiningDate ? `Joins ${formatIsoDate(row.joiningDate)}` : 'No joining date'}
                {row.validUntil && (row.status === 'sent' || row.status === 'viewed') ? ` · open until ${formatDate(row.validUntil, { timeZone: 'Asia/Dhaka' })}` : ''}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
